import React, { useEffect } from "react";
import PropTypes from "prop-types";
import cx from "classnames";
import { makeStyles } from "@material-ui/core/styles";

import GridContainer from "components/Grid/GridContainer.js";
import GridItem from "components/Grid/GridItem.js";
import Card from "components/Card/Card.js";
import CardBody from "components/Card/CardBody.js";
import { PnlSharedProvider } from "../../hooks/usePnlShared";
import Pnl from "./Pnl";

const useStyles = makeStyles(() => ({
  scrollableContainer: {
    maxHeight: "600px",
    overflowY: "auto",
    overflowX: "hidden",
    "&::-webkit-scrollbar": {
      width: "8px",
    },
    "&::-webkit-scrollbar-track": {
      background: "#f1f1f1",
    },
    "&::-webkit-scrollbar-thumb": {
      background: "#888",
      borderRadius: "4px",
    },
    "&::-webkit-scrollbar-thumb:hover": {
      background: "#555",
    },
  },
}));

const PnlDimensionDetail = ({
  pinnedName,
  pinnedLineName,
  pinnedDimension,
  pinnedDimensionData,
  pinnedDimensionLoading,
  filtersNames,
  getMonthsListFromLastMonth,
  plsValues,
  netRevenueAnchor,
  serveMarginAnchor,
  revenueAnchors,
  volumeUnit,
  volumeL12M,
  currencyUnit,
  showValueType,
  oom,
  drillDownsOpen,
  setDrillDownsOpen,
  metaLoaded,
  classes,
  i18n,
  t,
}) => {
  const localClasses = useStyles();

  useEffect(() => {
    if (pinnedName && !drillDownsOpen.includes(pinnedName)) {
      setDrillDownsOpen([...drillDownsOpen, pinnedName]);
    }
  }, [pinnedName, drillDownsOpen, setDrillDownsOpen]);

  const dimensionalStructure = {
    current: [
      {
        meta_name: pinnedName,
        name: pinnedLineName,
        level: 1,
        is_parent: false,
        parent: null,
        isTotal: true,
      },
      ...Object.keys(pinnedDimensionData).map((dimValue) => ({
        meta_name: `${pinnedName}_${dimValue}`,
        name: dimValue,
        level: 2,
        is_parent: false,
        parent: pinnedName,
      })),
    ],
  };

  const dimPlValues = {};
  [...Array(13)].forEach((_, idx) => {
    if (idx > 0) {
      const year_month = getMonthsListFromLastMonth[idx - 1];
      const year_month_str = String(year_month);
      dimPlValues[idx] = {};

      if (plsValues[idx] && plsValues[idx][pinnedName]) {
        dimPlValues[idx][pinnedName] = [
          plsValues[idx][pinnedName],
          plsValues[idx][netRevenueAnchor.current],
          plsValues[idx].volume,
        ];
      }

      Object.entries(pinnedDimensionData).forEach(([dimValue, monthsData]) => {
        if (monthsData[year_month_str]) {
          const metaName = `${pinnedName}_${dimValue}`;
          dimPlValues[idx][metaName] = [
            monthsData[year_month_str].value,
            monthsData[year_month_str].net_revenue,
            monthsData[year_month_str].volume,
          ];
        }
      });
    }
  });

  return (
    <div style={{ marginTop: "15px", marginBottom: "0px" }}>
      <div className={localClasses.scrollableContainer}>
        <PnlSharedProvider
          plValues={dimPlValues}
          plStructure={dimensionalStructure}
          benchmark={null}
          netRevenueAnchor={netRevenueAnchor}
          serveMarginAnchor={serveMarginAnchor}
          oom={oom}
          t={t}
        >
          <GridContainer className={classes.plWrapperOuter} spacing={0}>
            {[...Array(13)].map((_, idx) => {
              const numPL = idx;
              const year_month =
                idx > 0 ? getMonthsListFromLastMonth[idx - 1] : 0;
              const year =
                idx > 0 ? (Math.floor(year_month / 100) % 100).toString() : "";
              const month = idx > 0 ? (year_month % 100).toString() : "";
              const plName =
                idx > 0
                  ? `${t("months." + month)}-${year}`
                  : pinnedDimension
                    ? filtersNames.current[pinnedDimension]?.[i18n.language] ||
                      pinnedDimension
                    : t("results.select_dimension");

              if (idx === 0 || dimPlValues[idx]) {
                return (
                  <GridItem
                    sm={idx === 0 ? "auto" : true}
                    key={idx}
                    zeroMinWidth
                    style={{ padding: "0 1px" }}
                    className={cx({
                      [classes.plDisplay]: true,
                      show: true,
                    })}
                  >
                    <Card style={{ marginTop: 0, marginBottom: 0 }}>
                      <CardBody style={{ padding: 0 }}>
                        <Pnl
                          plStructure={dimensionalStructure}
                          netRevenueAnchor={netRevenueAnchor}
                          serveMarginAnchor={serveMarginAnchor}
                          revenueAnchors={revenueAnchors}
                          numPL={numPL}
                          numPLs={13}
                          plName={plName}
                          drillDownsOpen={drillDownsOpen}
                          setDrillDownsOpen={setDrillDownsOpen}
                          timersAllowed={false}
                          isRefreshing={pinnedDimensionLoading}
                          plValues={dimPlValues}
                          volumeUnit={volumeUnit}
                          volumeL12M={volumeL12M}
                          currencyUnit={currencyUnit}
                          showValueType={showValueType}
                          isLoading={!metaLoaded}
                          nameOnly={idx === 0}
                          showColumns={
                            idx === 0
                              ? ["name"]
                              : showValueType === "percentage"
                                ? ["percentage"]
                                : ["val"]
                          }
                          nameAlignRight={idx > 0}
                          benchmark={null}
                          showPin={false}
                          oom={oom}
                          showValueTooltip={true}
                          hideVolume={true}
                        />
                      </CardBody>
                    </Card>
                  </GridItem>
                );
              }
              return null;
            })}
          </GridContainer>
        </PnlSharedProvider>
      </div>
    </div>
  );
};

PnlDimensionDetail.propTypes = {
  pinnedName: PropTypes.string.isRequired,
  pinnedLineName: PropTypes.string.isRequired,
  pinnedDimension: PropTypes.string,
  pinnedDimensionData: PropTypes.object.isRequired,
  pinnedDimensionLoading: PropTypes.bool.isRequired,
  filtersNames: PropTypes.object.isRequired,
  getMonthsListFromLastMonth: PropTypes.array.isRequired,
  plsValues: PropTypes.object.isRequired,
  netRevenueAnchor: PropTypes.object.isRequired,
  serveMarginAnchor: PropTypes.object.isRequired,
  revenueAnchors: PropTypes.array.isRequired,
  volumeUnit: PropTypes.object.isRequired,
  volumeL12M: PropTypes.object.isRequired,
  currencyUnit: PropTypes.string,
  showValueType: PropTypes.string.isRequired,
  oom: PropTypes.object.isRequired,
  drillDownsOpen: PropTypes.array.isRequired,
  setDrillDownsOpen: PropTypes.func.isRequired,
  metaLoaded: PropTypes.bool.isRequired,
  classes: PropTypes.object.isRequired,
  i18n: PropTypes.object.isRequired,
  t: PropTypes.func.isRequired,
};

export default PnlDimensionDetail;
